/**
 * DOM initials-entry modal — the remastered take on the old canvas
 * overlay. Three slots, A–Z via keyboard or on-screen steppers,
 * Enter/CLAIM submits, SKIP resolves null (no score submission).
 */

import { getLastInitials, setLastInitials } from '../runtime/identity.js';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function runInitialsEntry(score: number): Promise<string | null> {
  const modal = document.getElementById('initials-modal');
  const scoreEl = document.getElementById('initials-score');
  const slotsEl = document.getElementById('initials-slots');
  const claimBtn = document.getElementById('initials-claim');
  const skipBtn = document.getElementById('initials-skip');
  if (
    !(modal instanceof HTMLElement) ||
    !(scoreEl instanceof HTMLElement) ||
    !(slotsEl instanceof HTMLElement) ||
    !(claimBtn instanceof HTMLButtonElement) ||
    !(skipBtn instanceof HTMLButtonElement)
  ) {
    return Promise.resolve(getLastInitials() ?? 'AAA');
  }

  const seed = (getLastInitials() ?? 'AAA').padEnd(3, 'A').slice(0, 3);
  const letters: number[] = [...seed].map((ch) => {
    const idx = LETTERS.indexOf(ch);
    return idx >= 0 ? idx : 0;
  });
  let cursor = 0;

  scoreEl.textContent = String(Math.floor(score)).padStart(6, '0');

  const render = (): void => {
    const slots = Array.from(slotsEl.querySelectorAll<HTMLElement>('.initials__slot'));
    slots.forEach((slot, i) => {
      const letterEl = slot.querySelector<HTMLElement>('.initials__letter');
      if (letterEl) letterEl.textContent = LETTERS[letters[i] ?? 0] ?? 'A';
      slot.classList.toggle('is-active', i === cursor);
    });
  };

  return new Promise<string | null>((resolve) => {
    const finish = (value: string | null): void => {
      window.removeEventListener('keydown', onKey, true);
      slotsEl.removeEventListener('click', onSlotClick);
      claimBtn.removeEventListener('click', onClaim);
      skipBtn.removeEventListener('click', onSkip);
      modal.hidden = true;
      if (value) setLastInitials(value);
      resolve(value);
    };

    const onClaim = (): void => {
      finish(letters.map((n) => LETTERS[n] ?? 'A').join(''));
    };
    const onSkip = (): void => finish(null);

    const onKey = (e: KeyboardEvent): void => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        onClaim();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
      } else if (e.key === 'ArrowLeft') {
        cursor = (cursor + 2) % 3;
        render();
      } else if (e.key === 'ArrowRight') {
        cursor = (cursor + 1) % 3;
        render();
      } else if (e.key === 'ArrowUp') {
        letters[cursor] = ((letters[cursor] ?? 0) + 1) % LETTERS.length;
        render();
      } else if (e.key === 'ArrowDown') {
        letters[cursor] = ((letters[cursor] ?? 0) + LETTERS.length - 1) % LETTERS.length;
        render();
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        letters[cursor] = LETTERS.indexOf(e.key.toUpperCase());
        cursor = Math.min(2, cursor + 1);
        render();
      }
    };

    const onSlotClick = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      const slot = target?.closest<HTMLElement>('.initials__slot');
      if (!slot) return;
      const idx = Number(slot.dataset.slot ?? '0');
      if (target?.classList.contains('initials__step--up')) {
        letters[idx] = ((letters[idx] ?? 0) + 1) % LETTERS.length;
      } else if (target?.classList.contains('initials__step--down')) {
        letters[idx] = ((letters[idx] ?? 0) + LETTERS.length - 1) % LETTERS.length;
      }
      cursor = idx;
      render();
    };

    window.addEventListener('keydown', onKey, true);
    slotsEl.addEventListener('click', onSlotClick);
    claimBtn.addEventListener('click', onClaim);
    skipBtn.addEventListener('click', onSkip);

    render();
    modal.hidden = false;
    claimBtn.focus();
  });
}
